import { catalogs, pageAssets, selectCatalog } from "./catalog";
import { CatalogSurfaceController } from "./catalog-surface";
import { HighResolutionPageController } from "./high-resolution-pages";
import { formatPage, pageFromHash, readerUrl } from "./reader-state";
import "./styles.css";

const TRANSPARENT_PIXEL =
  "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";
const CHROME_TIMEOUT_MS = 2500;
const PORTRAIT_HINT_MS = 4200;

const appRoot = document.querySelector<HTMLDivElement>("#app");
if (!appRoot) throw new Error("Catalog app root was not found.");
const app: HTMLDivElement = appRoot;

function requiredElement<T extends Element>(selector: string): T {
  const element = app.querySelector<T>(selector);
  if (!element) throw new Error(`Required catalog element not found: ${selector}`);
  return element;
}

const requestedCatalogId = new URLSearchParams(window.location.search).get(
  "catalog",
);
const catalog = selectCatalog(requestedCatalogId);
const totalPages = catalog.pages.length;
const initialPage = pageFromHash(window.location.hash, totalPages);
const initialIndex = initialPage - 1;

document.title = `${catalog.title} | ${catalog.titleEn}`;
document
  .querySelector<HTMLMetaElement>('meta[name="description"]')
  ?.setAttribute(
    "content",
    `在线浏览${catalog.title}。View the ${catalog.titleEn} online.`,
  );

const menuIcon = `
  <svg aria-hidden="true" viewBox="0 0 24 24">
    <path d="M5 7h14M5 12h14M5 17h14" />
  </svg>`;
const closeIcon = `
  <svg aria-hidden="true" viewBox="0 0 24 24">
    <path d="m7 7 10 10M17 7 7 17" />
  </svg>`;
const arrowIcon = `
  <svg aria-hidden="true" viewBox="0 0 24 24">
    <path d="M5 12h14m-5-5 5 5-5 5" />
  </svg>`;

const catalogSwitchLinks = catalogs
  .map((item) => {
    const current = item.id === catalog.id;
    const href = readerUrl(
      window.location.pathname,
      window.location.search,
      item.id,
      1,
      item.pages.length,
    );
    return `
      <a
        class="catalog-switch-link${current ? " is-current" : ""}"
        data-catalog-switch="${item.id}"
        href="${href}"
        aria-label="${item.title} / ${item.titleEn}"
        ${current ? 'aria-current="page"' : ""}>
        <span class="catalog-switch-copy">
          <strong>${item.title}</strong>
          <small lang="en">${item.titleEn}</small>
        </span>
        <span class="catalog-switch-state">
          ${
            current
              ? '<span>当前</span><small lang="en">CURRENT</small>'
              : arrowIcon
          }
        </span>
      </a>`;
  })
  .join("");

const slides = catalog.pages
  .map((page) => {
    const assets = pageAssets(catalog, page.number, import.meta.env.BASE_URL);
    return `
      <figure
        class="catalog-page"
        data-catalog-page="${page.number}"
        data-page-load-state="pending"
        role="group"
        aria-busy="false"
        aria-label="第 ${page.number} 页，共 ${totalPages} 页 / Page ${page.number} of ${totalPages}">
        <div class="page-load-feedback" data-page-load-feedback aria-hidden="true">
          <span class="page-load-line"></span>
          <p>
            <span>第 ${String(page.number).padStart(2, "0")} 页正在加载</span>
            <small lang="en">LOADING PAGE ${String(page.number).padStart(2, "0")}</small>
          </p>
        </div>
        <button
          class="page-retry"
          type="button"
          data-page-retry="${page.number}"
          aria-label="重新加载第 ${page.number} 页 / Retry page ${page.number}"
          hidden>
          <span>页面载入失败，请重试</span>
          <small lang="en">PAGE ${String(page.number).padStart(2, "0")} · TAP TO RETRY</small>
        </button>
        <img
          data-page-image
          data-src="${assets.small}"
          data-srcset="${assets.srcset}"
          data-high-src="${assets.high}"
          data-high-resolution="responsive"
          src="${TRANSPARENT_PIXEL}"
          alt="${page.alt}"
          width="${catalog.width}"
          height="${catalog.height}"
          draggable="false"
          decoding="async" />
      </figure>`;
  })
  .join("");

const thumbnails = catalog.pages
  .map((page) => {
    const assets = pageAssets(catalog, page.number, import.meta.env.BASE_URL);
    return `
      <button
        class="thumbnail-button"
        type="button"
        data-thumbnail-page="${page.number}"
        aria-label="前往第 ${page.number} 页 / Go to page ${page.number}">
        <img
          data-thumbnail-image
          data-src="${assets.small}"
          src="${TRANSPARENT_PIXEL}"
          alt=""
          width="960"
          height="720"
          loading="lazy"
          decoding="async" />
        <span>${String(page.number).padStart(2, "0")}</span>
      </button>`;
  })
  .join("");

const initialBackdrop = pageAssets(
  catalog,
  initialPage,
  import.meta.env.BASE_URL,
).small;

app.innerHTML = `
  <main
    class="catalog-shell"
    data-catalog-shell
    data-chrome-visible="true"
    data-menu-open="false"
    data-thumbnails-open="false"
    data-catalog-id="${catalog.id}"
    data-ready="false">
    <div class="catalog-backdrop" aria-hidden="true">
      <img class="is-active" data-backdrop-image="0" src="${initialBackdrop}" alt="" />
      <img data-backdrop-image="1" alt="" />
    </div>

    <div
      class="catalog-reader"
      data-catalog-reader
      data-active-page="${initialPage}"
      data-zoom-scale="1"
      tabindex="0"
      role="region"
      aria-label="${catalog.title} / ${catalog.titleEn}">
      <div class="catalog-stage" data-catalog-stage>${slides}</div>
    </div>

    <div class="catalog-loader" data-catalog-loader aria-live="polite">
      <span></span>
      <p>画册载入中 <small lang="en">LOADING CATALOG</small></p>
    </div>

    <div class="catalog-chrome" aria-label="画册控制 / Catalog controls">
      <button
        class="menu-trigger"
        type="button"
        data-menu-trigger
        aria-label="打开菜单 / Open menu"
        aria-controls="catalog-menu"
        aria-expanded="false">
        ${menuIcon}
      </button>
      <button
        class="page-counter"
        type="button"
        data-page-counter
        data-thumbnails-trigger
        aria-label="打开缩略图，当前第 ${initialPage} 页，共 ${totalPages} 页 / Open thumbnails, page ${initialPage} of ${totalPages}"
        aria-controls="catalog-thumbnails"
        aria-expanded="false">
        <span data-page-counter-text aria-live="polite">
          ${formatPage(initialPage, totalPages)}
        </span>
      </button>
    </div>

    <div
      class="menu-layer"
      data-menu-layer
      data-open="false"
      aria-hidden="true"
      inert>
      <aside
        class="catalog-menu"
        id="catalog-menu"
        role="dialog"
        aria-modal="true"
        aria-labelledby="catalog-menu-title">
        <button class="menu-close" type="button" data-menu-close aria-label="关闭菜单 / Close menu">
          ${closeIcon}
        </button>
        <p class="menu-eyebrow">${catalog.eyebrow}</p>
        <h1 id="catalog-menu-title">${catalog.title}</h1>
        <p class="menu-title-en" lang="en">${catalog.titleEn}</p>
        <div class="menu-rule"></div>
        <nav
          class="catalog-navigation"
          data-catalog-navigation
          aria-label="画册列表 / Catalogs">
          <div class="menu-section-heading">
            <span>画册</span>
            <small lang="en">CATALOGS</small>
          </div>
          <div class="catalog-switch-list">${catalogSwitchLinks}</div>
        </nav>
        <a class="website-link" href="${catalog.websiteUrl}">
          <span class="website-link-copy">
            <small>
              <span>官方网站</span>
              <span lang="en">OFFICIAL WEBSITE</span>
            </small>
            <strong>返回蓝宇机械官网</strong>
            <span lang="en">Lanyu Machinery Website</span>
          </span>
          ${arrowIcon}
        </a>
      </aside>
    </div>

    <div
      class="thumbnail-layer"
      data-thumbnail-layer
      data-open="false"
      aria-hidden="true"
      inert>
      <section
        class="thumbnail-panel"
        id="catalog-thumbnails"
        role="dialog"
        aria-modal="true"
        aria-labelledby="thumbnail-panel-title">
        <header class="thumbnail-header">
          <div>
            <p><span>页面导航</span><span lang="en">PAGE OVERVIEW</span></p>
            <h2 id="thumbnail-panel-title">
              选择页面 <small lang="en">Select a page</small>
            </h2>
          </div>
          <button
            class="thumbnail-close"
            type="button"
            data-thumbnail-close
            aria-label="关闭缩略图 / Close thumbnails">
            ${closeIcon}
          </button>
        </header>
        <div class="thumbnail-grid" data-thumbnail-grid>${thumbnails}</div>
      </section>
    </div>

    <div
      class="orientation-hint"
      data-orientation-hint
      role="status"
      aria-live="polite"
      hidden>
      <span class="phone-icon" aria-hidden="true"></span>
      <span class="orientation-copy">
        <strong>横屏阅读更清晰</strong>
        <small lang="en">Rotate for a clearer view</small>
      </span>
      <button type="button" data-orientation-close aria-label="关闭横屏提示 / Close orientation hint">${closeIcon}</button>
    </div>
  </main>`;

const shell = requiredElement<HTMLElement>("[data-catalog-shell]");
const reader = requiredElement<HTMLElement>("[data-catalog-reader]");
const stage = requiredElement<HTMLElement>("[data-catalog-stage]");
const menuTrigger = requiredElement<HTMLButtonElement>("[data-menu-trigger]");
const menuLayer = requiredElement<HTMLElement>("[data-menu-layer]");
const menuClose = requiredElement<HTMLButtonElement>("[data-menu-close]");
const counter = requiredElement<HTMLButtonElement>("[data-page-counter]");
const counterText = requiredElement<HTMLElement>("[data-page-counter-text]");
const thumbnailLayer = requiredElement<HTMLElement>("[data-thumbnail-layer]");
const thumbnailClose = requiredElement<HTMLButtonElement>(
  "[data-thumbnail-close]",
);
const thumbnailGrid = requiredElement<HTMLElement>("[data-thumbnail-grid]");
const loader = requiredElement<HTMLElement>("[data-catalog-loader]");
const orientationHint = requiredElement<HTMLElement>(
  "[data-orientation-hint]",
);
const orientationClose = requiredElement<HTMLButtonElement>(
  "[data-orientation-close]",
);

const images = [
  ...reader.querySelectorAll<HTMLImageElement>("[data-page-image]"),
];
const pages = [
  ...reader.querySelectorAll<HTMLElement>("[data-catalog-page]"),
];
const retryButtons = [
  ...reader.querySelectorAll<HTMLButtonElement>("[data-page-retry]"),
];
const backdropImages = [
  ...app.querySelectorAll<HTMLImageElement>("[data-backdrop-image]"),
];
const thumbnailButtons = [
  ...app.querySelectorAll<HTMLButtonElement>("[data-thumbnail-page]"),
];
const thumbnailImages = [
  ...app.querySelectorAll<HTMLImageElement>("[data-thumbnail-image]"),
];
let chromeTimer: number | undefined;
let previousFocus: HTMLElement | null = null;
let activeBackdropSlot = 0;
let currentBackdropPage = initialPage;
let backdropRequest = 0;

type PageLoadState = "pending" | "loading" | "ready" | "error";

function setPageLoadState(index: number, state: PageLoadState): void {
  const page = pages[index];
  const retryButton = retryButtons[index];
  if (!page || !retryButton) return;

  page.dataset.pageLoadState = state;
  page.setAttribute("aria-busy", String(state === "loading"));
  retryButton.hidden = state !== "error";
}

function hydrateImage(index: number, force = false): void {
  const image = images[index];
  if (!image || (!force && image.dataset.hydrated === "true")) return;

  const requestId = String(Number(image.dataset.loadRequest ?? "0") + 1);
  image.dataset.loadRequest = requestId;
  image.dataset.hydrated = "true";
  setPageLoadState(index, "loading");

  const isCurrentRequest = (): boolean =>
    image.dataset.loadRequest === requestId;
  const clearListeners = (): void => {
    image.removeEventListener("load", handleLoad);
    image.removeEventListener("error", handleError);
  };
  const handleLoad = (): void => {
    if (!isCurrentRequest() || image.naturalWidth <= 1) return;
    clearListeners();
    image.dataset.pageImageReady = "true";
    setPageLoadState(index, "ready");
  };
  const handleError = (): void => {
    if (!isCurrentRequest()) return;
    clearListeners();
    image.dataset.hydrated = "false";
    image.dataset.pageImageReady = "false";
    setPageLoadState(index, "error");
  };

  image.addEventListener("load", handleLoad);
  image.addEventListener("error", handleError);

  image.src = image.dataset.src ?? image.src;
  image.srcset = image.dataset.srcset ?? "";
  image.sizes = "100vw";
  image.loading = index === initialIndex ? "eager" : "lazy";
  image.fetchPriority = index === initialIndex ? "high" : "auto";

  if (image.complete && image.naturalWidth > 1) {
    window.queueMicrotask(handleLoad);
  }
}

function hydrateAround(index: number): void {
  [index - 1, index, index + 1].forEach((pageIndex) =>
    hydrateImage(pageIndex),
  );
}

retryButtons.forEach((button, index) => {
  button.addEventListener("pointerdown", (event) => event.stopPropagation());
  button.addEventListener("click", (event) => {
    event.stopPropagation();
    hydrateImage(index, true);
  });
});

function setChromeVisible(visible: boolean, autoHide = true): void {
  window.clearTimeout(chromeTimer);
  shell.dataset.chromeVisible = String(visible);
  const overlayOpen =
    shell.dataset.menuOpen === "true" ||
    shell.dataset.thumbnailsOpen === "true";
  if (visible && autoHide && !overlayOpen) {
    chromeTimer = window.setTimeout(() => {
      shell.dataset.chromeVisible = "false";
    }, CHROME_TIMEOUT_MS);
  }
}

function updateBackdrop(page: number): void {
  if (page === currentBackdropPage) return;

  const request = ++backdropRequest;
  const nextSlot = activeBackdropSlot === 0 ? 1 : 0;
  const currentImage = backdropImages[activeBackdropSlot];
  const nextImage = backdropImages[nextSlot];
  if (!currentImage || !nextImage) return;

  const activate = (): void => {
    if (request !== backdropRequest) return;
    currentImage.classList.remove("is-active");
    nextImage.classList.add("is-active");
    activeBackdropSlot = nextSlot;
    currentBackdropPage = page;
  };

  nextImage.src = pageAssets(catalog, page, import.meta.env.BASE_URL).small;
  nextImage.dataset.page = String(page);
  if (nextImage.complete && nextImage.naturalWidth > 1) {
    activate();
  } else {
    nextImage.addEventListener("load", activate, { once: true });
  }
}

function updatePage(page: number): void {
  counterText.textContent = formatPage(page, totalPages);
  counter.setAttribute(
    "aria-label",
    `打开缩略图，当前第 ${page} 页，共 ${totalPages} 页 / Open thumbnails, page ${page} of ${totalPages}`,
  );
  hydrateAround(page - 1);
  updateBackdrop(page);

  thumbnailButtons.forEach((button, index) => {
    const active = index === page - 1;
    button.classList.toggle("is-current", active);
    if (active) {
      button.setAttribute("aria-current", "page");
    } else {
      button.removeAttribute("aria-current");
    }
  });

  const nextUrl = readerUrl(
    window.location.pathname,
    window.location.search,
    catalog.id,
    page,
    totalPages,
  );
  if (`${window.location.pathname}${window.location.search}${window.location.hash}` !== nextUrl) {
    window.history.replaceState(null, "", nextUrl);
  }
  setChromeVisible(true);
}

function openMenu(): void {
  previousFocus = document.activeElement as HTMLElement | null;
  shell.dataset.menuOpen = "true";
  menuLayer.dataset.open = "true";
  menuLayer.setAttribute("aria-hidden", "false");
  menuLayer.inert = false;
  menuTrigger.setAttribute("aria-expanded", "true");
  setChromeVisible(true, false);
  window.requestAnimationFrame(() => menuClose.focus({ preventScroll: true }));
}

function closeMenu(): void {
  shell.dataset.menuOpen = "false";
  menuLayer.dataset.open = "false";
  menuLayer.setAttribute("aria-hidden", "true");
  menuLayer.inert = true;
  menuTrigger.setAttribute("aria-expanded", "false");
  previousFocus?.focus();
  setChromeVisible(true);
}

function hydrateThumbnails(): void {
  thumbnailImages.forEach((image) => {
    if (image.dataset.hydrated === "true") return;
    image.src = image.dataset.src ?? image.src;
    image.dataset.hydrated = "true";
  });
}

function centerThumbnailInGrid(index: number): void {
  const button = thumbnailButtons[index];
  if (!button) return;

  const gridRect = thumbnailGrid.getBoundingClientRect();
  const buttonRect = button.getBoundingClientRect();
  thumbnailGrid.scrollLeft +=
    buttonRect.left -
    gridRect.left -
    (gridRect.width - buttonRect.width) / 2;
  thumbnailGrid.scrollTop +=
    buttonRect.top -
    gridRect.top -
    (gridRect.height - buttonRect.height) / 2;
}

function openThumbnails(): void {
  previousFocus = document.activeElement as HTMLElement | null;
  hydrateThumbnails();
  shell.dataset.thumbnailsOpen = "true";
  thumbnailLayer.dataset.open = "true";
  thumbnailLayer.setAttribute("aria-hidden", "false");
  thumbnailLayer.inert = false;
  counter.setAttribute("aria-expanded", "true");
  setChromeVisible(true, false);

  window.requestAnimationFrame(() => {
    thumbnailClose.focus({ preventScroll: true });
    centerThumbnailInGrid(surface.activeIndex);
  });
}

function closeThumbnails(): void {
  shell.dataset.thumbnailsOpen = "false";
  thumbnailLayer.dataset.open = "false";
  thumbnailLayer.setAttribute("aria-hidden", "true");
  thumbnailLayer.inert = true;
  counter.setAttribute("aria-expanded", "false");
  previousFocus?.focus();
  setChromeVisible(true);
}

function handleOverlayKeys(event: KeyboardEvent): void {
  const activeLayer =
    shell.dataset.menuOpen === "true"
      ? menuLayer
      : shell.dataset.thumbnailsOpen === "true"
        ? thumbnailLayer
        : null;
  if (!activeLayer) return;

  if (event.key === "Escape") {
    event.preventDefault();
    if (activeLayer === menuLayer) closeMenu();
    else closeThumbnails();
    return;
  }
  if (event.key !== "Tab") return;

  const focusable = [
    ...activeLayer.querySelectorAll<HTMLElement>("button, a[href]"),
  ];
  const first = focusable[0];
  const last = focusable.at(-1);
  if (!first || !last) return;

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

hydrateAround(initialIndex);

const firstImage = images[initialIndex];
const markReady = (): void => {
  shell.dataset.ready = "true";
  loader.setAttribute("aria-hidden", "true");
};
if (firstImage?.complete) {
  markReady();
} else {
  firstImage?.addEventListener("load", markReady, { once: true });
  firstImage?.addEventListener("error", markReady, { once: true });
}

const highResolutionPages = new HighResolutionPageController({
  images,
  minimumScale: 1.2,
});

const surface = new CatalogSurfaceController({
  viewport: reader,
  stage,
  pageCount: totalPages,
  pageAspectWidth: catalog.width,
  pageAspectHeight: catalog.height,
  initialIndex,
  minScale: 1,
  maxScale: 4,
  doubleTapScale: 2.5,
  onPageChange: (index) => updatePage(index + 1),
  onTransformChange: (snapshot) =>
    highResolutionPages.update(snapshot.activeIndex, snapshot.scale),
  onTap: () => {
    const visible = shell.dataset.chromeVisible === "true";
    setChromeVisible(!visible);
  },
});

window.addEventListener(
  "pagehide",
  () => {
    surface.destroy();
    highResolutionPages.destroy();
  },
  { once: true },
);

menuTrigger.addEventListener("click", openMenu);
menuClose.addEventListener("click", closeMenu);
menuLayer.addEventListener("click", (event) => {
  if (event.target === menuLayer) closeMenu();
});
counter.addEventListener("click", openThumbnails);
thumbnailClose.addEventListener("click", closeThumbnails);
thumbnailLayer.addEventListener("click", (event) => {
  if (event.target === thumbnailLayer) closeThumbnails();
});
thumbnailButtons.forEach((button) => {
  button.addEventListener("click", () => {
    const page = Number.parseInt(button.dataset.thumbnailPage ?? "1", 10);
    if (page !== surface.activeIndex + 1) surface.goToPage(page - 1);
    closeThumbnails();
  });
});
document.addEventListener("keydown", handleOverlayKeys);

document.addEventListener("keydown", (event) => {
  if (
    shell.dataset.menuOpen === "true" ||
    shell.dataset.thumbnailsOpen === "true" ||
    event.altKey ||
    event.ctrlKey ||
    event.metaKey
  ) {
    return;
  }
  if (event.key === "ArrowRight" || event.key === "PageDown") {
    event.preventDefault();
    surface.goToPage(surface.activeIndex + 1);
  } else if (event.key === "ArrowLeft" || event.key === "PageUp") {
    event.preventDefault();
    surface.goToPage(surface.activeIndex - 1);
  } else if (event.key === "Home") {
    event.preventDefault();
    surface.goToPage(0);
  } else if (event.key === "End") {
    event.preventDefault();
    surface.goToPage(totalPages - 1);
  }
});

window.addEventListener("hashchange", () => {
  const requestedPage = pageFromHash(window.location.hash, totalPages);
  if (requestedPage !== surface.activeIndex + 1) {
    surface.goToPage(requestedPage - 1);
  }
});

const portraitQuery = window.matchMedia(
  "(max-width: 767px) and (orientation: portrait)",
);
let portraitTimer: number | undefined;

function dismissOrientationHint(): void {
  window.clearTimeout(portraitTimer);
  orientationHint.hidden = true;
}

function updateOrientationHint(): void {
  if (!portraitQuery.matches) {
    dismissOrientationHint();
    return;
  }
  if (window.sessionStorage.getItem("lanyu-catalog-orientation-hint")) return;

  window.sessionStorage.setItem("lanyu-catalog-orientation-hint", "shown");
  orientationHint.hidden = false;
  portraitTimer = window.setTimeout(dismissOrientationHint, PORTRAIT_HINT_MS);
}

orientationClose.addEventListener("click", dismissOrientationHint);
portraitQuery.addEventListener("change", updateOrientationHint);
updateOrientationHint();
updatePage(initialPage);
